import BridgedDropdown from 'components/common/bridged-dropdown';
import type { Auth0UserProfile } from 'auth0-js';
import UserSwitcher from 'components/layout/topbar/switcher/user-switcher';
import { css, useTheme } from '@emotion/react';
import { links } from 'constants/external-links';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import { observer } from 'mobx-react';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'types/theme.types';
import type { TrackingData } from 'utils/analytics.utils';
import { EventNames } from 'utils/analytics.utils';
import TrackableLink from 'components/common/trackable-link';
import TrackbleButton from 'components/common/trackble-button';
import Tooltip from 'components/common/tooltip';
import { isSAMLUser } from 'utils/user.utils';
import { isFreeTierOrganization } from 'utils/organizations.utils';
import { AppNotifications } from 'components/app-notifications/app-notifications';
import { KbarSearchButton } from 'components/kbar/kbar-search-button';
import { IconButton } from 'components/common/icon-button';
import useStores from 'hooks/use-stores.hooks';
import useIsMobile from 'hooks/use-is-mobile';
import { DownOutlined, MenuOutlined, ReadOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar } from 'antd';
import type { BreadcrumbSelector } from 'components/breadcrumbs/use-breadcrumbs';
import AppThemeSwitchButton from 'components/theme-switcher';
import { ReactComponent as LogoIcon } from 'assets/images/logo-without-text.svg';
import { ReactComponent as SignBlack } from 'assets/images/env-zero-sign-black.svg';
import { ReactComponent as SignWhite } from 'assets/images/env-zero-sign-white.svg';
import TopbarOrganizationSelector from 'components/layout/topbar/topbar-organization-selector';
import TopBarProjectSelector from 'components/layout/topbar/topbar-project-selector';

interface Props {
  profile?: Auth0UserProfile;
  title?: string;
  breadcrumbs?: BreadcrumbSelector;
}

const scheduleDemoTracking: TrackingData = {
  eventName: EventNames.TOP_BAR_SCHEDULE_DEMO,
  eventProperties: {}
};

const docsLinkTracking: TrackingData = {
  eventName: EventNames.TOP_BAR_DOCS_CLICK,
  eventProperties: {}
};

const TopBarView: React.FunctionComponent<Props> = ({ profile, title }) => {
  const {
    organizationsStore: { currentOrganization },
    navbarStore
  } = useStores();

  const { isMobile } = useIsMobile();
  const theme = useTheme();

  const navigate = useNavigate();

  const openSideBar = useCallback(() => {
    if (isMobile) {
      navbarStore.toggleOpen();
    }
  }, [isMobile, navbarStore]);

  const handleAppLogoClick = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const showDemoButton = !isSAMLUser(profile) && isFreeTierOrganization(currentOrganization);

  /**
   * Figma's cluster order. Same controls either way, so switch-off renders the identical tree.
   * Schedule-demo has no Figma treatment and is kept before Docs (D1 extrapolation).
   */
  const rightCluster = (userProfile: Auth0UserProfile) => {
    const themeToggle = <AppThemeSwitchButton key="theme" />;
    const search = !isMobile && <KbarSearchButton key="search" />;
    const notifications = !isMobile && <AppNotifications key="notifications" />;
    const demo = !isMobile && showDemoButton && (
      <TrackbleButton
        key="demo"
        data-e2e="demo-button"
        type={'primary'}
        href={links.DEMO.SCHEDULE_DEMO}
        isExternalLink={true}
        trackingData={scheduleDemoTracking}>
        <FormattedMessage id="schedule.demo.text" />
      </TrackbleButton>
    );
    // An icon button like its cluster neighbours (UI-polish review: "replace Docs with icon").
    // Tracking stays on TrackbleButton; the tooltip carries the name the label no longer shows.
    // Legacy keeps the text link.
    const docs = theme.isNewDesign ? (
      <Tooltip key="docs" titleId="docs.link.text" placement="bottom">
        <TrackbleButton
          type="link"
          href={links.docs.ROOT}
          isExternalLink
          icon={<ReadOutlined />}
          aria-label="Docs"
          data-e2e="docs-link"
          trackingData={docsLinkTracking}
        />
      </Tooltip>
    ) : (
      <DocContainer key="docs">
        <TrackableLink
          data-e2e="docs-link"
          messageId={'docs.link.text'}
          url={links.docs.ROOT}
          trackingData={docsLinkTracking}
        />
      </DocContainer>
    );
    const user = (
      <BridgedDropdown
        key="user"
        dropdownRender={originNode => (
          <>
            <UserSwitcher />
            {originNode}
          </>
        )}
        placement="bottomRight"
        trigger={['click']}>
        <UserContainer data-e2e="user-menu-dropdown">
          {/* Figma's is an *Avatar* instance, documented as antd's own: 32px, circle, and a fallback
              glyph when the picture will not load, which a bare img has no answer for. */}
          {theme.isNewDesign ? (
            <Avatar size={32} src={userProfile.picture} icon={<UserOutlined />} />
          ) : (
            <UserAvatar src={userProfile.picture} />
          )}
          {theme.isNewDesign ? (
            <IconButton icon={DownOutlined} size="small" dataE2e="user-menu-chevron" ariaLabel="user menu" />
          ) : (
            <ArrowDown />
          )}
        </UserContainer>
      </BridgedDropdown>
    );

    // Figma's cluster is unevenly spaced, which one flex gap cannot express. Search leads the cluster
    // (UI-polish review: "move search to left").
    return theme.isNewDesign
      ? [
          search,
          <ControlGroup key="controls">
            {themeToggle}
            {notifications}
            {demo}
            {docs}
          </ControlGroup>,
          <AccountGroup key="account">{user}</AccountGroup>
        ]
      : [themeToggle, search, notifications, demo, docs, user];
  };

  return (
    <Container>
      <TitleRow>
        {isMobile && (
          <IconWrapper onClick={openSideBar}>
            <MenuOutlined />
          </IconWrapper>
        )}
        <SubContainer>
          <LeftContainer>
            {!isMobile && (
              <LogoIconContainer onClick={handleAppLogoClick}>
                <TopBarLogo />
              </LogoIconContainer>
            )}
            {currentOrganization ? (
              <ContextSwitcherContainer>
                <TopbarOrganizationSelector />
                <TopBarProjectSelector />
              </ContextSwitcherContainer>
            ) : (
              <Title data-e2e="topbar-title" isMobile={isMobile}>
                {title}
              </Title>
            )}
          </LeftContainer>
          {profile && <RightContainer>{rightCluster(profile)}</RightContainer>}
        </SubContainer>
      </TitleRow>
    </Container>
  );
};

/**
 * The Logo page ships one mark per background and none that works on both, so this reads the colour
 * mode. The glyph is a filled path, not a stroked icon that would take currentColor.
 */
const TopBarLogo: React.FC = () => {
  const theme = useTheme();

  if (!theme.isNewDesign) return <StyledLogoIcon data-e2e="topbar-logo" />;

  return <StyledSign as={theme.mode === 'dark' ? SignWhite : SignBlack} data-e2e="topbar-logo" />;
};

// Two blocks, each where its declarations sat: emotion emits in source order and the parity gate is
// byte-exact, so merging them fails it.
const Container = styled.div`
  ${legacyOnly(
    theme => css`
      background: ${theme.primaryWhite};
    `
  )}
  ${newDesignOnly(css`
    background: var(--surface-main-cards);
  `)}
  align-self: stretch;
  z-index: 1;
  ${legacyOnly(
    theme => css`
      border-bottom: 1px solid ${theme.separatorGray};
    `
  )}
  ${newDesignOnly(css`
    border-bottom: 1px solid var(--border-primary);
  `)}
`;

// Exported for topbar-view.spec, which asserts each design builds its bar height its own way.
export const TitleRow = styled.div`
  /* Stays above padding-right, where the unbranched declaration sat: the parity fixture pins this
     rule's bytes in source order, and moving it fails the gate on a reorder alone. */
  ${legacyOnly(
    theme => css`
      min-height: ${theme.layout.topBarHeight};
    `
  )}
  padding-right: 20px;
  ${legacyOnly(css`
    padding-left: 16px;
  `)}
  ${newDesignOnly(css`
    padding-top: 14px;
    padding-bottom: 14px;
    padding-left: 24px;
  `)}
  display: flex;
  align-items: center;
  z-index: 100;
  flex: none;
`;

const SubContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex: 1;
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 18px;
`;

const RightContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  ${legacyOnly(css`
    gap: 16px;
  `)}
  ${newDesignOnly(css`
    gap: var(--spacing-xxs);
  `)}
`;

// New-design only, so neither needs a branch: the legacy cluster renders its children ungrouped.
const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xxxs);
`;

const AccountGroup = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xl);
`;

const Title = styled.span<{ isMobile: boolean }>`
  color: ${({ theme }) => theme.textColor};
  font-size: ${({ isMobile }) => (isMobile ? '1.3em' : '1.5em')};
  ${({ isMobile }) => (isMobile ? 'padding-left: 1em' : '')};
  font-weight: 600;
  letter-spacing: -0.15px;
`;

const ArrowDown = styled.div`
  margin-left: 5px;
  margin-bottom: 2px;
  position: relative;
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: ${({ theme }) => `4px solid ${theme.primaryBlack}`};
`;

// Switch-off only: the new design renders Docs as a Button, which owns the box it used to hand-roll.
const DocContainer = styled.div`
  padding-top: 3px;
`;

const UserContainer = styled.div`
  cursor: pointer;
  ${newDesignOnly(css`
    display: flex;
    align-items: center;
    gap: var(--spacing-xxxxs);
    color: var(--icon-primary);
  `)}
`;

// Switch-off only. The wrappers stay put: the parity fixture pins this rule's bytes in source order.
const UserAvatar = styled.img`
  ${legacyOnly(css`
    height: 28px;
    width: 28px;
  `)}
  border-radius: 50%;
  ${legacyOnly(css`
    margin-right: 5px;
  `)}
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 1.8em;
  flex: none;
`;

const LogoIconContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;
  cursor: pointer;
  ${legacyOnly(css`
    padding-top: 10px;
  `)}
`;

const StyledLogoIcon = styled(LogoIcon)`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
`;

// `height: auto`, not Figma's rounded 21px: pinning both axes squashes the mark by half a pixel.
const StyledSign = styled.svg`
  width: 32px;
  height: auto;
  flex-shrink: 0;
`;

// Each branch where its declarations sat, for the byte-exact parity gate.
const ContextSwitcherContainer = styled.div`
  display: flex;
  align-items: center;
  ${legacyOnly(css`
    gap: 4px;
  `)}
  /* Figma sets the two selector buttons 2px apart. */
  ${newDesignOnly(css`
    gap: var(--spacing-xxxxs);
  `)}
  margin-left: 8px;
  ${legacyOnly(
    theme => css`
      margin-top: 2px;
      color: ${theme.subtitleGray};
    `
  )}
  ${newDesignOnly(css`
    color: var(--text-heading);
  `)}
`;

export default observer(TopBarView);
